import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { CanvasIndicator } from "@zapier/ai-toolkit/client";
import { createPortal } from "react-dom";
import {
  copilotLoadingNodeIdAtom,
  currentToolMessageAtom,
} from "@zapier/copilot-tools";
import { sideBarWidthAtom } from "@zapier/canvas-state";
import styles from "./CanvasCopilotIndicator.module.css";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import classNames from "classnames";

interface CanvasIndicatorProps {
  nodeId: string;
  inFocusMode?: boolean;
  isClosing?: boolean;
}

export function CanvasCopilotIndicator({
  nodeId,
  inFocusMode = false,
  isClosing = false,
}: CanvasIndicatorProps) {
  const [loadingNodeId, setLoadingNodeId] = useAtom(copilotLoadingNodeIdAtom);
  const copilot = useCopilotChat();
  const sideBarWidth = useAtomValue(sideBarWidthAtom);
  const [showWithDelay, setShowWithDelay] = useState(false);

  const handleStopCopilot = () => {
    setLoadingNodeId(null);
    // This stops the copilot chat streaming
    copilot.stop?.();
  };

  const toolMessage = useAtomValue(currentToolMessageAtom);
  const displayMessage = toolMessage?.displayMessage;

  // Show the indicator whenever this node is loading, regardless of message
  const shouldShow = loadingNodeId === nodeId;

  // Handle delay for focus mode appearance
  useEffect(() => {
    if (inFocusMode && shouldShow && !isClosing) {
      // 200ms delay before showing in focus mode
      const timer = setTimeout(() => {
        setShowWithDelay(true);
      }, 200);

      return () => clearTimeout(timer);
    } else {
      // Reset delay state when not in focus mode or not showing
      setShowWithDelay(false);
    }
  }, [inFocusMode, shouldShow, isClosing]);

  // Determine if we should actually render the indicator
  const shouldRender = shouldShow && (!inFocusMode || showWithDelay);

  // Hide immediately when closing in focus mode to prevent flash
  // or when copilot is stopped
  if (
    !shouldRender ||
    (inFocusMode && isClosing) ||
    copilot.status === "ready"
  ) {
    return null;
  }

  // Use displayMessage if available, otherwise show default loading message
  const messageToShow = displayMessage || "Copilot is working...";

  // Calculate dynamic positioning for focus mode
  const focusModeStyle = inFocusMode
    ? {
        left: `calc(${sideBarWidth}px + (100vw - ${sideBarWidth}px) / 2)`,
      }
    : {};

  const indicatorContent = (
    <div
      className={classNames(styles.container, {
        [styles.inFocusMode]: inFocusMode,
      })}
      style={focusModeStyle}
      role="status"
      aria-live="polite"
      aria-label={`Copilot is processing: ${messageToShow}`}
    >
      <CanvasIndicator
        status="loading"
        title={messageToShow}
        onStop={handleStopCopilot}
      />
    </div>
  );

  // Use portal in focus mode to render above iframe stacking context
  if (inFocusMode && typeof document !== "undefined") {
    return createPortal(indicatorContent, document.body);
  }

  return indicatorContent;
}
